import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { SidebarCollapseService } from 'src/app/core/services/sidebar-collapse.service';
import { NotificationItem, SseNotificationService } from 'src/app/core/services/sse-notification.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() pageTitle = 'Dashboard';

  notificationsDropdownOpen = false;
  profileDropdownOpen = false;
  currentUserId: number | null = null;
  userInitials = 'U';
  userName = 'Utilisateur';
  userEmail = '';
  userRole = '';
  zoneNom = '';
  sidebarCollapsed = false;
  sidebarOpen = false;
  private sidebarOverlay?: HTMLElement;
  private sseSub?: Subscription;

  notificationsRecentes: NotificationItem[] = [];

  constructor(
    private authService: AuthService,
    private router: Router,
    private dialog: MatDialog,
    private sidebarCollapseService: SidebarCollapseService,
    private sse: SseNotificationService
  ) {}

  ngOnInit(): void {
    this.loadUserInfo();
    window.addEventListener('storage', this.loadUserInfo);
    this.sidebarCollapsed = this.sidebarCollapseService.collapsed;
    this.sidebarCollapseService.collapsed$.subscribe(v => this.sidebarCollapsed = v);
    this.sseSub = this.sse.notifications$.subscribe(liste => {
      this.notificationsRecentes = liste.slice(0, 5);
    });
  }

  onMenuToggle(): void {
    if (window.innerWidth <= 1100) {
      this.sidebarOpen = !this.sidebarOpen;
      this.syncSidebarState();
    } else {
      this.sidebarCollapseService.toggle();
    }
  }

  closeSidebar = (): void => {
    if (!this.sidebarOpen) return;
    this.sidebarOpen = false;
    this.syncSidebarState();
  };

  private syncSidebarState(): void {
    document.body.classList.toggle('sidebar-open', this.sidebarOpen);
    if (!this.sidebarOverlay) {
      this.sidebarOverlay = document.createElement('div');
      this.sidebarOverlay.className = 'sidebar-overlay';
      this.sidebarOverlay.addEventListener('click', this.closeSidebar);
      document.body.appendChild(this.sidebarOverlay);
    }
    this.sidebarOverlay.classList.toggle('visible', this.sidebarOpen);
  }

  ngOnDestroy(): void {
    this.sseSub?.unsubscribe();
    window.removeEventListener('storage', this.loadUserInfo);
    document.body.classList.remove('sidebar-open');
    if (this.sidebarOverlay) {
      this.sidebarOverlay.removeEventListener('click', this.closeSidebar);
      this.sidebarOverlay.remove();
    }
  }

  loadUserInfo = (): void => {
    this.currentUserId = this.authService.obtenirIdUtilisateurCourant();
    const user = this.authService.obtenirUtilisateurCourant() || {};

    const firstName = user.firstName || user.prenom || user.firstname || '';
    const lastName = user.lastName || user.nom || user.lastname || '';
    const email = user.email || user.username || '';

    if (firstName && lastName) {
      this.userInitials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
      this.userName = `${firstName} ${lastName}`;
    } else if (firstName) {
      this.userInitials = firstName.charAt(0).toUpperCase();
      this.userName = firstName;
    } else if (lastName) {
      this.userInitials = lastName.charAt(0).toUpperCase();
      this.userName = lastName;
    } else if (email) {
      this.userInitials = email.charAt(0).toUpperCase();
      this.userName = email.split('@')[0];
    } else {
      this.userInitials = 'U';
      this.userName = 'Utilisateur';
    }

    this.userEmail = email || '';
    this.userRole = (user.role || '').toString().toUpperCase();
    this.zoneNom = user.zoneNom || '';
  };

  estZoneVisible(): boolean {
    return (this.userRole === 'RESPONSABLE_ZONE' || this.userRole === 'COMPTABLE_ZONE') && !!this.zoneNom;
  }

  libelleRoleZone(): string {
    if (this.userRole === 'RESPONSABLE_ZONE') return 'Resp. zone';
    if (this.userRole === 'COMPTABLE_ZONE') return 'Comptable';
    return '';
  }

  toggleNotifications(): void {
    this.notificationsDropdownOpen = !this.notificationsDropdownOpen;
    this.profileDropdownOpen = false;
  }

  closeNotifications(): void {
    this.notificationsDropdownOpen = false;
  }

  toggleProfile(): void {
    this.profileDropdownOpen = !this.profileDropdownOpen;
    this.notificationsDropdownOpen = false;
  }

  get nbNonLues(): number {
    return this.notificationsRecentes.filter(n => !n.lu).length;
  }

  hasUnread(): boolean {
    return this.notificationsRecentes.some(n => !n.lu);
  }

  marquerNotificationLue(id: string): void {
    this.sse.marquerCommeLue(id);
  }

  marquerToutLu(): void {
    this.sse.marquerToutesCommeLues();
    this.notificationsDropdownOpen = false;
  }

  formatTempsDropdown(date: Date): string {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "À l'instant";
    if (mins < 60) return `Il y a ${mins} min`;
    const h = Math.floor(mins / 60);
    if (h < 24) return `Il y a ${h} h`;
    const j = Math.floor(h / 24);
    return j === 1 ? 'Hier' : `Il y a ${j} j`;
  }

  iconeClass(type: string): string {
    const m: Record<string, string> = {
      NOUVELLE_COMMANDE:   'fal fa-box-open',
      STATUT_COMMANDE:     'fal fa-sync-alt',
      AFFECTATION_LIVREUR: 'fal fa-motorcycle',
      LIVRAISON_CONFIRMEE: 'fal fa-check-circle',
      ALERTE_DOCUMENT:     'fal fa-exclamation-triangle',
    };
    return m[type] ?? 'fal fa-bell';
  }

  couleurType(type: string): string {
    const m: Record<string, string> = {
      NOUVELLE_COMMANDE:   'notif-info',
      STATUT_COMMANDE:     'notif-info',
      AFFECTATION_LIVREUR: 'notif-success',
      LIVRAISON_CONFIRMEE: 'notif-success',
      ALERTE_DOCUMENT:     'notif-warning',
    };
    return m[type] ?? 'notif-info';
  }

  logout(): void {
    this.profileDropdownOpen = false;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '95vw', maxWidth: '440px',
      data: {
        title: 'Déconnexion',
        message: 'Voulez-vous vraiment vous déconnecter ?',
        confirmLabel: 'Se déconnecter',
        confirmIcon: '🚪',
        confirmStyle: 'primary',
        icon: '🚪'
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.sse.arreter();
        this.authService.deconnexion();
        this.router.navigate(['/connexion']);
      }
    });
  }

  closeProfileDropdown(): void {
    this.profileDropdownOpen = false;
  }
}
